import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import {
  SG_GET_CHAT_ROOM_V2,
  SG_CREATE_CHAT_ROOM_V2,
  SG_FETCH_CHAT_ROOM_V2,
  SG_DELETE_CHAT_ROOM,
  DISCONNECT_WEBSOCKET_V2,
  SAGA_CONNECT_WEBSOCKET_V2,
  MESSAGE_WEBSOCKET,
  SAGA_MESSAGE_WEBSOCKET_V2,
  CLEAR_CHAT_MESSAGES,
  CLEAR_CREATED_ROOM,
  CHAT_ROOM_PAGE,
  SET_NEW_FILTERS,
  START_NEW_CHAT,
  CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD
} from "constants/actions";

import {
  generateTopicName,
  addPromptToHistoryIfNeeded,
} from "../chatHelpers";

const getPromptObjectDataFromParams = (params) => {
  // Example parser for "file" or "pulse" data sources
  const fileCardData = params?.dataSources?.filter(
    (source) => source.type === "prompt"
  );
  return fileCardData;
};

const getCardDataFromParams = (params) => {
  // Example parser for "file" or "pulse" data sources
  const fileCardData =
    params?.dataSources
      ?.filter((source) => source.type === "file")
      ?.map((source) => ({
        type: "file",
        id: source.id,
        title: source?.name,
        status: 22,
      })) || [];

  const factorCardData =
    params?.tags?.map((source) => ({
      type: "culture",
      id: source,
      title: source,
    })) || [];

  const outcomeCardData =
    params?.outcomes?.map((source) => ({
      type: "outcome",
      id: source,
      title: source,
    })) || [];

  const selectedPulses =
    params?.dataSources
      ?.filter((source) => source.type === "pulse")
      ?.map((source) => ({
        type: "pulse",
        id: source.id,
        title: source?.name || source?.title,
        status: 22,
      })) || [];

  return [
    ...fileCardData,
    ...factorCardData,
    ...outcomeCardData,
    ...selectedPulses,
  ];
};

// Create the context
const CopilotDataContext = createContext();

export const useCopilotDataContext = () => useContext(CopilotDataContext);

export const CopilotDataProvider = ({ children }) => {
  const lastScrollPositionRef = useRef(null);
  const isSmoothScrollingRef = useRef(false);

  const dispatch = useDispatch();
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [chatUserName, setChatUserName] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ignoredMessages, setIgnoredMessages] = useState([]);

  const [chatStarted, setChatStarted] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [pendingInitialMessage, setPendingInitialMessage] = useState(null);
  const [selectedParams, setSelectedParams] = useState(null);
  const [messagesByRoom, setMessagesByRoom] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [initialPendingId, setInitialPendingId] = useState(null);
  const [usedPrompt, setUsedPrompt] = useState(null);
  const [messagePending, setMessagePending] = useState(false);
  const [feedbackLength, setFeedbackLength] = useState(0);
  const createdRoom = useSelector(
    (state) => state.ai_chatv2?.created,
    shallowEqual
  );

  const updatedRoom = useSelector(
    (state) => state.ai_chatv2?.updated,
    shallowEqual
  );

  const chatToken = useSelector(
    (state) => state.auth?.chat_token,
    shallowEqual
  );
  const usrEmployee = useSelector(
    (state) => state.employees?.userEmp,
    shallowEqual
  );

  const chatRooms = useSelector(
    (state) => state.ai_chatv2?.chat_topics || [],
    shallowEqual
  );
  const currentRoom = useSelector(
    (state) => state.ai_chatv2?.chatData,
    shallowEqual
  );

  const wsConnected = useSelector(
    (state) => state.ai_websocketv2?.isConnected,
    shallowEqual
  );
  const wsError = useSelector(
    (state) => state.ai_websocketv2?.error,
    shallowEqual
  );
  const messages = useSelector((state) => state.ai_websocketv2?.messages);

  // A ref for message scrolling if needed
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    // Save current scroll position
    const currentScrollTop = document.documentElement.scrollTop;
    const currentScrollHeight = document.documentElement.scrollHeight;

    // Wait for content to potentially reflow
    setTimeout(() => {
      const newScrollHeight = document.documentElement.scrollHeight;
      // If content height changed, adjust scroll position to maintain relative position
      if (newScrollHeight > currentScrollHeight) {
        const heightDiff = newScrollHeight - currentScrollHeight;
        window.scrollTo({
          top: currentScrollTop + heightDiff,
          behavior: "smooth",
        });
      }

      // Then scroll to bottom
      if (messagesEndRef?.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 50);
  };

  const isValidMessageForChat = (data, roomId) => {
    /**
     * In your old ChatWindow code, you had logic like:
     * (!data?.web_chat_id && only one message key) ||
     *   data?.web_chat_id === room?.id ||
     *   (data?.web_chat_id && isNaN(data.web_chat_id)) ||
     *   data?.channel?.id
     *
     * Adjust it as needed. The simplest check is:
     */
    if (data?.web_chat_id && !roomId) return true;
    if (!roomId) return false;
    if (data?.web_chat_id === roomId) return true;
    return false;
  };

  const addOrUpdateMessageInContext = (roomId, newMsg) => {
    saveScrollPosition();

    const pendingUserId = `usr-${initialPendingId}`;
    // If it's a user message, we need to check if it's the pending message
    // If it is, we can skip it.
    if (newMsg.from === "user" && newMsg.id === pendingUserId) {
      return;
    }

    if (messagesByRoom?.[roomId]?.length === 1 && newMsg.from === "user") {
      const lastMessage = messagesByRoom?.[roomId]?.[0];
      if (lastMessage.from === "user" && lastMessage.status === "complete") {
        setIgnoredMessages((prev) => [...prev, lastMessage.id]);
      }
      return;
    }

    //   If it is an ignored message, we can skip it.
    if (ignoredMessages.includes(newMsg.id)) {
      return;
    }

    setMessagesByRoom((prev) => {
      const newState = { ...prev };
      const existingRoomMessages = newState[roomId] || [];

      // Check if we already have a message with that same ID
      const index = existingRoomMessages.findIndex((m) => m.id === newMsg.id);

      if (index >= 0) {
        // Update the existing
        const old = existingRoomMessages[index];
        existingRoomMessages[index] = {
          ...old,
          message: newMsg.message,
          status: newMsg.status,
          from: newMsg.from || old.from,
          type: "response", // Mark it as assistant response
        };
      } else {
        // Insert a new assistant message
        existingRoomMessages.push({
          id: newMsg.id,
          message: newMsg.message,
          from: newMsg.from || "assistant",
          status: newMsg.status,
          type: "response",
        });
      }

      newState[roomId] = existingRoomMessages;
      return newState;
    });

    // After setting state, determine if we should scroll to bottom
    const shouldScrollToBottom =
      newMsg.from === "assistant" ||
      newMsg.status === "complete" ||
      newMsg.status === "streaming";

    // Restore position or scroll to bottom after state update
    // restoreScrollPosition(shouldScrollToBottom);
  };

  // -------------------------------------------------------------------
  // Main effect to handle incoming WebSocket messages
  // -------------------------------------------------------------------
  useEffect(() => {
    // If there's no connected socket or no new messages object, do nothing
    if (!wsConnected || !messages) return;

    let awaitingMessage = false;
    // Loop over the dictionary
    Object.entries(messages).forEach(([key, msg]) => {
      if (isValidMessageForChat(msg, selectedRoomId)) {
        // Override the msg's `id` with the dictionary key:
        addOrUpdateMessageInContext(selectedRoomId, {
          ...msg,
          id: key, // e.g. "usr-msg-1739991001848" or "ast-msg-1739991001848"
        });

        // If it's a user message, we need to check if it's the pending message
        // if the most recent message is a user message, then are awaiting a response
        if (msg.from === "user") {
          awaitingMessage = true;
        } else {
          awaitingMessage = false;
        }
      }
    });

    setMessagePending(awaitingMessage);
    // Scroll down after merging
    // scrollToBottom();
  }, [wsConnected, messages, selectedRoomId]);

  useEffect(() => {
    setChatUserName(usrEmployee?.first_name + " " + usrEmployee?.last_name);
  }, [usrEmployee]);

  useEffect(() => {
    if (createdRoom?.id) {
      // First update the room list
      dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [createdRoom, dispatch]);

  useEffect(() => {
    if (updatedRoom) {
      dispatch({ type: CLEAR_CREATED_ROOM });
      dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [dispatch, updatedRoom]);

  useEffect(() => {
    dispatch({ type: SG_GET_CHAT_ROOM_V2, payload: `page=${currentPage}` });
  }, [dispatch, currentPage]);

  // -------------------------------------------------------------------
  // Utility to parse room data from the store
  // -------------------------------------------------------------------
  const handleFetchedRoomData = (roomData) => {
    const { id, interaction_history = [], params = {}, feedback_length = 0 } = roomData;

    setFeedbackLength(params?.feedback_length);
    // Example: convert params into "card data"
    const paramsFormatted = getCardDataFromParams(params);
    const paramsObjectData = getPromptObjectDataFromParams(params);

    setSelectedParams(paramsFormatted);

    let presetPrompt = "";

    if (paramsObjectData?.length > 0) {
      setUsedPrompt(paramsObjectData[0]);
      const promptObject = paramsObjectData[0];
      presetPrompt = promptObject?.question;
    } else {
      setUsedPrompt(null);
    }

    // Flatten the "prompt → response" pairs
    const parsedMessages = interaction_history.flatMap((interaction, i) => {
      // Below parsedMessages creates the message history,
      // IF the presetPrompt is not empty, we can replace the initial message with the promptQuery
      // We do this to hide our Intellectual Property from the user.
      const questionObj = {
        id: interaction?.id,
        type: interaction?.from === "user" ? "prompt" : "response",
        message: interaction?.message || "",
        error: interaction?.status || null,
      };

      if (i === 0 && presetPrompt) {
        questionObj.message = presetPrompt;
      }

      if (interaction?.from === "assistant") {
        const responseObj = {
          id: interaction?.id,
          type: "response",
          message: interaction?.message,
          error: interaction.status || null,
          server: {},
        };
        return [questionObj, responseObj];
      }

      return [questionObj];
    });

    // Remove any duplicate id's
    const uniqueMessages = parsedMessages.filter(
      (msg, index, self) => index === self.findIndex((t) => t.id === msg.id)
    );

    setMessagesByRoom((prev) => ({
      ...prev,
      [id]: uniqueMessages,
    }));
  };

  // Step 1 (New Chat): Initiate chat
  const initiateChat = (promptQuery, feedbackData, feedbackParams) => {
    dispatch({ type: CLEAR_CHAT_MESSAGES }); // Clear message history
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });

    const messageId = uuidv4();

    let usedQuery = promptQuery;

    const payload = {
      token: chatToken,
      name: generateTopicName(usedQuery),
      initial_message: {
        id: messageId,
        message: usedQuery,
        from: "user",
        type: "user",
        user: chatUserName,
        timestamp: new Date().toISOString(),
      },
      contextData: feedbackData,
      params: feedbackParams,
      status: "active",
    };

    const initialMessageId = `msg-${Date.now()}`;
    const initialMessage = {
      id: initialMessageId,
      message: usedQuery,
      from: "user",
      username: chatUserName,
      timestamp: new Date().toISOString(),
    };
    setMessagePending(true);

    setPendingInitialMessage(initialMessage);

    dispatch({
      type: SG_CREATE_CHAT_ROOM_V2,
      payload: payload,
    });

    setChatStarted(true);
    setPromptText("");
  };

  // Step 2: Handle room creation and WebSocket connection
  useEffect(() => {
    if (!createdRoom?.id || !pendingInitialMessage) return;

    const roomId = createdRoom.id;
    // First disconnect any existing connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });
    // Set new room ID
    setSelectedRoomId(roomId);
    // Connect WebSocket
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: roomId },
    });
    // Fetch room details
    dispatch({
      type: SG_FETCH_CHAT_ROOM_V2,
      payload: { id: roomId },
    });
  }, [createdRoom, pendingInitialMessage, selectedRoomId]);

  // Step 3: Send initial message after connection
  useEffect(() => {
    if (wsConnected && pendingInitialMessage && selectedRoomId) {
      const messagePayload = {
        id: `msg-${Date.now()}`,
        message: pendingInitialMessage.message.trim(),
        web_chat_id: selectedRoomId,
        from: "user",
        username: chatUserName,
        timestamp: new Date().toISOString(),
        type: "user",
      };

      // First dispatch the message to local state
      dispatch({
        type: MESSAGE_WEBSOCKET,
        payload: messagePayload,
      });

      // Then send through websocket
      dispatch({
        type: SAGA_MESSAGE_WEBSOCKET_V2,
        payload: messagePayload,
      });
      // Cleanup
      dispatch({ type: CLEAR_CREATED_ROOM });
      setPendingInitialMessage(null);
    }
  }, [wsConnected, pendingInitialMessage, selectedRoomId, chatUserName]);

  useEffect(() => {
    if (currentRoom) {
      handleFetchedRoomData(currentRoom);
    }
  }, [currentRoom, selectedRoomId]);

  // Add this function to save scroll position before updates
  const saveScrollPosition = () => {
    if (isSmoothScrollingRef.current) return;

    lastScrollPositionRef.current = {
      position: window.scrollY,
      height: document.body.scrollHeight,
    };
  };

  // Add this function to restore scroll position after updates
  const restoreScrollPosition = (shouldScrollToBottom = false) => {
    // Short delay to allow DOM to update
    setTimeout(() => {
      if (!lastScrollPositionRef.current) return;

      const { position, height } = lastScrollPositionRef.current;
      const newHeight = document.body.scrollHeight;
      const heightDiff = newHeight - height;

      if (shouldScrollToBottom) {
        // Scroll to bottom with smooth behavior
        isSmoothScrollingRef.current = true;
        if (messagesEndRef?.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
        setTimeout(() => {
          isSmoothScrollingRef.current = false;
        }, 500); // Duration slightly longer than scroll animation
      } else if (heightDiff > 0) {
        // Maintain relative position if content expanded
        window.scrollTo(0, position + heightDiff);
      } else {
        // Otherwise restore original position
        window.scrollTo(0, position);
      }

      lastScrollPositionRef.current = null;
    }, 10);
  };

  // Regular Room Selection Handler
  const handleRoomSelect = useCallback(
    (roomId) => {
      if (selectedRoomId) {
        dispatch({
          type: DISCONNECT_WEBSOCKET_V2,
          payload: { web_chat_id: selectedRoomId },
        });
      }

      // Set new room ID
      setSelectedRoomId(roomId);

      // Connect WebSocket for existing room
      dispatch({
        type: SAGA_CONNECT_WEBSOCKET_V2,
        payload: { web_chat_id: roomId },
      });

      // Fetch room history
      dispatch({
        type: SG_FETCH_CHAT_ROOM_V2,
        payload: { id: roomId },
      });

      // Set chat started
      setChatStarted(true);
    },
    [dispatch, selectedRoomId]
  );

  const handleDeleteRoom = (roomId) => {
    const isActiveRoom = selectedRoomId === roomId;

    // Disconnect the current websocket connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });

    // Delete the room
    dispatch({ type: SG_DELETE_CHAT_ROOM, payload: { id: roomId } });

    // If we're deleting the active room, reset everything
    if (isActiveRoom) {
      setSelectedRoomId(null);
      setPromptText("");
      setChatStarted(false);
      setSelectedParams(null);

      // Reset params.
      dispatch({ type: CLEAR_CHAT_MESSAGES });
      dispatch({ type: SET_NEW_FILTERS, payload: [] });
      dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
      dispatch({ type: START_NEW_CHAT });
    }

    // Refresh list of rooms after delete
    dispatch({ type: SG_GET_CHAT_ROOM_V2 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const cleanup = () => {
    setSelectedParams(null);
    dispatch({ type: START_NEW_CHAT });
  };

  // Function to start a new conversation
  const startNewConversation = () => {
    // setChatHistory([]);
    setSelectedRoomId(null);
    setPromptText("");
    setChatStarted(false);

    // Reset params.
    dispatch({ type: CLEAR_CHAT_MESSAGES });
    dispatch({ type: SET_NEW_FILTERS, payload: [] });
    dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
    // Disconnect the current websocket connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });

    dispatch({
      type: START_NEW_CHAT,
    });

    setSelectedParams(null);
  };

  const handleSendMessage = useCallback(
    (messageText) => {
      if (!wsConnected || !selectedRoomId) {
        console.error("Cannot send message:", {
          connected: wsConnected,
          hasWebChatId: !!selectedRoomId,
        });
        return;
      }
      const messagePayload = {
        id: `msg-${Date.now()}`,
        message: messageText.trim(),
        web_chat_id: selectedRoomId,
        from: "user",
        username: chatUserName,
        timestamp: new Date().toISOString(),
        status: "complete",
      };

      dispatch({
        type: MESSAGE_WEBSOCKET,
        payload: messagePayload,
      });
      dispatch({
        type: SAGA_MESSAGE_WEBSOCKET_V2,
        payload: messagePayload,
      });
      addPromptToHistoryIfNeeded(chatHistory, setChatHistory, promptText);
      setMessagePending(true);

      setPromptText("");
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    },
    [wsConnected, selectedRoomId, chatUserName, dispatch]
  );

  const reconnect = useCallback(() => {
    if (!selectedRoomId) {
      console.error("Cannot reconnect: No room ID selected");
      return;
    }

    // First disconnect any existing connection
    dispatch({
      type: DISCONNECT_WEBSOCKET_V2,
    });

    // Then reconnect using the current room ID
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET_V2,
      payload: { web_chat_id: selectedRoomId },
    });
  }, [dispatch, selectedRoomId]);

  return (
    <CopilotDataContext.Provider
      value={{
        chatRooms,
        currentRoom,
        chatStarted,
        promptText,
        chatUserName,
        wsConnected,
        wsError,
        chatToken,
        handleRoomSelect,
        handleDeleteRoom,
        initiateChat,
        handlePageChange,
        setPromptText,
        startNewConversation,
        selectedRoomId,
        messagesEndRef,
        messagesByRoom,
        handleSendMessage,
        pendingInitialMessage,
        messagePending,
        selectedParams,
        streamingMsg: messages,
        initialPendingId,
        usedPrompt,
        feedbackLength,
        setSelectedParams,
        cleanup,
        reconnect,
        isWebSocketConnected: wsConnected,
      }}
    >
      {children}
    </CopilotDataContext.Provider>
  );
};
